import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import useQueryState from "../../../hooks/useQueryState";
import SelectCriticalWindow from "../../Select/SelectCriticalWindow/SelectCriticalWindow";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { ANOMALY_LAYERS } from "../../../enums/layers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { INFO_CONTENT } from "../../../enums/info";
import { useUIStore } from "../../../store/ui";

const AccordionStyled = styled((props: AccordionProps) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  width: "100%",
  backgroundColor: "white",

  /* remove ALL borders */
  border: "none",

  "&:not(:last-child)": {
    borderBottom: "none",
  },

  "&::before": {
    display: "none",
  },

  /* expanded state */
  "&.Mui-expanded": {
    margin: 0,
  },
  "&:last-of-type .MuiAccordionSummary-root:not(.Mui-expanded)": {
    borderBottom: "none",
  },
}));

const Layer = ({
  season,
  displayName,
  months,
  dekads,
  icon,
  openDialog,
  description,
  chip,
  parameters,
  active,
}: {
  season: string;
  displayName: string;
  months: string[];
  dekads: string[];
  icon: ReactNode;
  openDialog: (e: React.MouseEvent<HTMLButtonElement>, type: string) => void;
  description?: string;
  chip?: string;
  parameters?: {
    name: string;
    value: number;
  }[];
  active: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    queryState: { layers },
    setQueryState,
  } = useQueryState();

  const editLayer = useUIStore((s) => s.editLayer);
  const toggleEditLayer = useUIStore((s) => s.toggleEditLayer);

  useEffect(() => {
    if (
      editLayer.length > 0 &&
      editLayer.includes(`${displayName}-${season}`)
    ) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [editLayer, displayName, season]);

  const selectedLayer = useMemo(
    () =>
      layers.find(
        (layer) => layer.displayName === displayName && layer.season === season,
      ),
    [layers],
  );

  const handleSwitchLayer = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    event.stopPropagation();

    setQueryState((prev) => {
      const isSameLayer = (layer: any) =>
        layer.displayName === displayName && layer.season === season;

      const existingLayer = prev.layers.find(isSameLayer);

      // ------------------------
      // TOGGLE OFF
      // ------------------------
      if (existingLayer && existingLayer.active) {
        return {
          ...prev,
          layers: prev.layers.map((layer) =>
            isSameLayer(layer) ? { ...layer, active: false } : layer,
          ),
          payout: prev.payout,
        };
      }

      // ------------------------
      // TOGGLE ON
      // ------------------------

      const isSinglePerSeason = prev.payout.comparisonFocus === 1;

      let newLayers = prev.layers.map((layer) => {
        if (isSinglePerSeason && layer.season === season) {
          return { ...layer, active: false };
        }
        return layer;
      });

      if (existingLayer) {
        newLayers = newLayers.map((layer) =>
          isSameLayer(layer) ? { ...layer, active: true } : layer,
        );
      } else {
        newLayers = [
          ...newLayers,
          {
            displayName,
            months,
            dekads,
            season,
            parameters,
            active: true,
          },
        ];
      }

      return {
        ...prev,
        season: prev.season.includes(season)
          ? prev.season
          : [...prev.season, season],
        layers: newLayers,
        payout: prev.payout,
      };
    });
  };

  const handleChangeHybridParams = (
    event: SelectChangeEvent<string>,
    index: number,
  ) => {
    if (parameters) {
      if (selectedLayer?.parameters) {
        const updatedParameters = selectedLayer.parameters;
        updatedParameters[index].name = event.target.value;
        const updatedLayer = layers.filter(
          (layer) =>
            layer.displayName !== displayName || layer.season !== season,
        );
        setQueryState({
          layers: [
            ...updatedLayer,
            { ...selectedLayer, parameters: updatedParameters },
          ],
        });
      } else {
        const updatedParameters = parameters;
        updatedParameters[index].name = event.target.value;
        setQueryState({
          layers: [
            ...layers,
            {
              displayName,
              months,
              dekads,
              season,
              parameters: updatedParameters,
            },
          ],
        });
      }
    }
  };

  const handleInputHybridIndex = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    if (parameters) {
      if (selectedLayer?.parameters) {
        const updatedParameters = selectedLayer.parameters;
        updatedParameters[index].value = event.target.value;
        const updatedLayer = layers.filter(
          (layer) =>
            layer.displayName !== displayName || layer.season !== season,
        );
        setQueryState({
          layers: [
            ...updatedLayer,
            { ...selectedLayer, parameters: updatedParameters },
          ],
        });
      } else {
        const updatedParameters = parameters;
        updatedParameters[index].value = Number(event.target.value);
        setQueryState({
          layers: [
            ...layers,
            {
              displayName,
              months,
              dekads,
              season,
              parameters: updatedParameters,
            },
          ],
        });
      }
    }
  };

  return (
    <Stack>
      <AccordionStyled
        expanded={isExpanded}
        onChange={() => {
          const value = `${displayName}-${season}`;
          toggleEditLayer(value);
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            borderBottom: "0px solid black",
            border: "none",
            minHeight: 0,
            "&.Mui-expanded": {
              minHeight: 0,
            },
            "& .MuiAccordionSummary-content": {
              margin: 0,
              "&.Mui-expanded": {
                margin: 0,
              },
            },
          }}
        >
          <FormControlLabel
            onClick={(e) => e.stopPropagation()}
            control={
              <Switch
                checked={!!selectedLayer?.active}
                onClick={(e) => e.stopPropagation()}
                onChange={handleSwitchLayer}
                size="small"
                // disabled={Boolean(comparisonFocus === 0 && isFinancialIndicesStep)}
              />
            }
            label={
              <Box display="flex" gap="5px" alignItems="center">
                <Stack p={0.5} alignItems="center">
                  {icon}
                </Stack>
                <Typography variant="subtitle1">{displayName}</Typography>
                {chip && (
                  <Chip
                    label={<Typography variant="body3">{chip}</Typography>}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #8E24AA",
                      background: "#F3E5F5",
                      color: "#8E24AA",
                    }}
                  />
                )}

                <Tooltip title={INFO_CONTENT[displayName]?.description}>
                  <Info sx={{ color: "#6C6C89" }} fontSize="small" />
                </Tooltip>
              </Box>
            }
            labelPlacement="start"
            componentsProps={{
              typography: {
                fontSize: "16px",
              },
            }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              margin: 0,
              padding: 0,
              width: "100%",
            }}
          />
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingTop: 0,
            borderTop: "none",
          }}
        >
          {description && (
            <Typography variant="subtitle2">{description}</Typography>
          )}
          <Box mt={3}>
            <SelectCriticalWindow
              dekads={dekads}
              months={months}
              displayName={displayName}
              season={season}
            />
          </Box>
          {selectedLayer?.parameters &&
            // displayName === "Hybrid Index" &&
            parameters && (
              <Stack gap={3} mt={2}>
                {Array.from({ length: 2 }).map((each, index) => (
                  <Stack gap={2}>
                    <Typography variant="subtitle1">
                      Parameter {index + 1}
                    </Typography>
                    <FormControl>
                      <InputLabel id={`Parameter ${index + 1}`}>
                        Parameter {index + 1}
                      </InputLabel>
                      <Select
                        value={selectedLayer?.parameters?.[index]?.name}
                        label={`Parameter ${index + 1}`}
                        labelId={`Parameter ${index + 1}`}
                        onChange={(e) => handleChangeHybridParams(e, index)}
                        error={
                          index === 1 &&
                          selectedLayer?.parameters?.[0]?.name ===
                            selectedLayer?.parameters?.[1]?.name
                        }
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                            },
                          },
                        }}
                      >
                        {ANOMALY_LAYERS.slice(0, 3).map((layer, i) => (
                          <MenuItem
                            // disabled={
                            //   parameters[index === 0 ? 1 : 0].name ===
                            //   layer.displayName
                            // }
                            key={`layer-${layer.displayName}-${index}`}
                            value={layer.displayName}
                          >
                            {layer.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                      {index === 1 &&
                        selectedLayer?.parameters?.[0]?.name ===
                          selectedLayer?.parameters?.[1]?.name && (
                          <FormHelperText sx={{ color: "#fd3f75" }}>
                            Selected parameter should be diffrent
                          </FormHelperText>
                        )}
                    </FormControl>
                    {selectedLayer?.parameters && (
                      <FormControl>
                        <TextField
                          id={`input-${index + 1}`}
                          label={`Parameter ${index + 1} Percentage`}
                          type="number"
                          value={selectedLayer?.parameters[index].value}
                          onChange={(e) => handleInputHybridIndex(e, index)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                          error={
                            index === 1 &&
                            !!selectedLayer?.parameters[1].value &&
                            Number(selectedLayer?.parameters[0].value) +
                              Number(selectedLayer?.parameters[1].value) !==
                              100
                          }
                          helperText={
                            index === 1 &&
                            !!selectedLayer?.parameters[1].value &&
                            Number(selectedLayer?.parameters[0].value) +
                              Number(selectedLayer?.parameters[1].value) !==
                              100
                              ? "The total sum of the parameters must be 100%."
                              : undefined
                          }
                        />
                      </FormControl>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
        </AccordionDetails>
      </AccordionStyled>
    </Stack>
  );
};

export default Layer;
